import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { errText } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { LetterTemplates } from "../components/LetterTemplates";
import { BackupCodes } from "../components/BackupCodes";
import { GoogleLogo, MicrosoftOutlookLogo, ShieldCheck, CalendarPlus, ArrowsClockwise, ShieldWarning } from "@phosphor-icons/react";

function PrivacyRequests() {
  const qc = useQueryClient();
  const { data: reqs } = useQuery({ queryKey: ["delete-requests"], queryFn: () => api.get("/privacy/delete-requests").then((r) => r.data) });
  const resolve = useMutation({
    mutationFn: ({ id, action }) => api.post(`/privacy/delete-requests/${id}/resolve`, { action }),
    onSuccess: () => { toast.success("Request resolved"); qc.invalidateQueries({ queryKey: ["delete-requests"] }); },
    onError: (e) => toast.error(errText(e)),
  });
  const pending = (reqs || []).filter((r) => r.status === "pending");
  return (
    <div className="rounded-lg border border-zinc-200 bg-white p-4" data-testid="privacy-requests">
      <p className="text-sm font-medium text-zinc-900 flex items-center gap-1.5 mb-2"><ShieldWarning size={16} /> GDPR / CCPA deletion requests</p>
      {!pending.length && <p className="text-xs text-zinc-400 italic">No pending requests.</p>}
      {pending.map((r) => (
        <div key={r.id} className="flex items-center justify-between py-2 border-b border-zinc-100 last:border-0" data-testid={`delete-req-${r.id}`}>
          <div>
            <p className="text-xs text-zinc-800">{r.user_name} <span className="text-zinc-400 font-mono">{r.user_email}</span></p>
            <p className="text-[11px] text-zinc-500">{r.reason || "No reason given"} · {new Date(r.created_at).toLocaleDateString()}</p>
          </div>
          <div className="flex gap-2">
            <Button data-testid={`delete-req-approve-${r.id}`} size="sm" variant="destructive" className="h-7 text-[11px]" disabled={resolve.isPending} onClick={() => resolve.mutate({ id: r.id, action: "approve" })}>Approve</Button>
            <Button data-testid={`delete-req-deny-${r.id}`} size="sm" variant="outline" className="h-7 text-[11px]" disabled={resolve.isPending} onClick={() => resolve.mutate({ id: r.id, action: "deny" })}>Deny</Button>
          </div>
        </div>
      ))}
    </div>
  );
}

function ProviderCard({ provider, label, Icon, info }) {
  const connect = useMutation({
    mutationFn: () => api.get(`/integrations/${provider}/connect`),
    onSuccess: ({ data }) => { window.location.href = data.url; },
    onError: (e) => toast.error(errText(e)),
  });
  return (
    <div className="rounded-lg border border-zinc-200 bg-white p-4 flex items-center justify-between">
      <div className="flex items-center gap-3">
        <Icon size={24} className="text-zinc-700" />
        <div>
          <p className="text-sm font-medium text-zinc-900">{label}</p>
          <p className="text-xs text-zinc-500">
            {!info?.configured ? "Not configured — add OAuth credentials to backend/.env"
              : info?.connected ? `Connected as ${info.email || "account"}` : "Send email & sync deadlines to calendar"}
          </p>
        </div>
      </div>
      {info?.connected ? (
        <span className="text-[10px] bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-sm px-2 py-1">CONNECTED</span>
      ) : (
        <Button data-testid={`connect-${provider}-btn`} size="sm" variant="outline" disabled={!info?.configured || connect.isPending} onClick={() => connect.mutate()}>
          Connect
        </Button>
      )}
    </div>
  );
}

export default function Settings() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const { data: status } = useQuery({ queryKey: ["integrations"], queryFn: () => api.get("/integrations/status").then((r) => r.data) });
  const { data: users } = useQuery({ queryKey: ["users"], queryFn: () => api.get("/users").then((r) => r.data), enabled: user?.role === "admin" });

  const syncCal = useMutation({
    mutationFn: () => api.post("/integrations/calendar/sync-deadlines"),
    onSuccess: ({ data }) => toast.success(`${data.events_created} deadline events created in your calendar`),
    onError: (e) => toast.error(errText(e)),
  });

  const resetMfa = useMutation({
    mutationFn: (id) => api.post(`/auth/mfa/reset/${id}`),
    onSuccess: () => { toast.success("MFA reset — user will re-enroll at next login"); qc.invalidateQueries({ queryKey: ["users"] }); },
    onError: (e) => toast.error(errText(e)),
  });

  const anyConnected = status?.google?.connected || status?.microsoft?.connected;

  return (
    <div className="p-8 space-y-6 fade-up max-w-3xl" data-testid="settings-page">
      <div>
        <h1 className="font-heading text-2xl font-medium tracking-tight text-zinc-900">Settings</h1>
        <p className="text-sm text-zinc-500 mt-1">Security & connected services</p>
      </div>

      <div className="rounded-lg border border-zinc-200 bg-white p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <ShieldCheck size={24} weight="fill" className="text-emerald-600" />
          <div>
            <p className="text-sm font-medium text-zinc-900">Multi-factor authentication</p>
            <p className="text-xs text-zinc-500">TOTP enforced for all roles at every login</p>
          </div>
        </div>
        <span data-testid="mfa-status-badge" className="text-[10px] bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-sm px-2 py-1">
          {user?.mfa_enabled ? "ENABLED" : "PENDING ENROLLMENT"}
        </span>
      </div>

      <div className="space-y-3">
        <p className="text-xs font-medium text-zinc-600 uppercase tracking-wide">Email & Calendar</p>
        <ProviderCard provider="google" label="Google Workspace (Gmail + Calendar)" Icon={GoogleLogo} info={status?.google} />
        <ProviderCard provider="microsoft" label="Microsoft 365 (Outlook + Calendar)" Icon={MicrosoftOutlookLogo} info={status?.microsoft} />
        <div className="rounded-lg border border-zinc-200 bg-white p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CalendarPlus size={24} className="text-zinc-700" />
            <div>
              <p className="text-sm font-medium text-zinc-900">Sync engagement deadlines</p>
              <p className="text-xs text-zinc-500">Create calendar events for all active engagement deadlines</p>
            </div>
          </div>
          <Button data-testid="sync-deadlines-btn" size="sm" variant="outline" disabled={!anyConnected || syncCal.isPending} onClick={() => syncCal.mutate()}>
            <ArrowsClockwise size={14} className="mr-1" /> {syncCal.isPending ? "Syncing…" : "Sync now"}
          </Button>
        </div>
      </div>

      <div className="space-y-3">
        <p className="text-xs font-medium text-zinc-600 uppercase tracking-wide">Client-facing documents</p>
        <LetterTemplates />
        <BackupCodes />
      </div>

      {(user?.role === "admin" || user?.role === "partner") && (
        <div className="space-y-3">
          <p className="text-xs font-medium text-zinc-600 uppercase tracking-wide">Privacy &amp; compliance</p>
          <PrivacyRequests />
        </div>
      )}

      {user?.role === "admin" && (
        <div className="space-y-3">
          <p className="text-xs font-medium text-zinc-600 uppercase tracking-wide">Users (admin)</p>
          <div className="rounded-lg border border-zinc-200 bg-white divide-y divide-zinc-100">
            {(users || []).map((u) => (
              <div key={u.id} className="flex items-center justify-between p-3">
                <div>
                  <p className="text-sm text-zinc-900">{u.name} <span className="text-[10px] uppercase text-zinc-400 ml-1">{u.role}</span></p>
                  <p className="text-xs text-zinc-500 font-mono">{u.email}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-[10px] rounded-sm px-1.5 py-0.5 border ${u.mfa_enabled ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-zinc-100 text-zinc-500 border-zinc-200"}`}>
                    {u.mfa_enabled ? "MFA ON" : "NOT ENROLLED"}
                  </span>
                  {u.mfa_enabled && (
                    <Button data-testid={`reset-mfa-btn-${u.id}`} size="sm" variant="ghost" className="h-7 text-xs" disabled={resetMfa.isPending} onClick={() => resetMfa.mutate(u.id)}>
                      Reset MFA
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
